(() => {
  if (customElements.get('hydration-upsell')) return;
  class HydrationUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.inputs = [...this.querySelectorAll('input[name="hydration_flavor"]')];
      this.productIds = new Set(this.inputs.map(input => String(input.dataset.productId)));
      this.strings = JSON.parse(this.querySelector('[data-hu-strings]').textContent);
      this.rootPath = this.dataset.root || '/';
      this.preview = this.dataset.themeRole !== 'main' && (new URLSearchParams(location.search).get('hydration_preview') === '1' || window.Shopify?.designMode);
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      if (this.preview) {
        this.inputs.forEach(input => { input.disabled = false; });
        this.querySelectorAll('.hu-unavailable').forEach(label => { label.hidden = true; });
      }
      this.form.addEventListener('submit', event => { event.preventDefault(); this.purchase(); }, options);
      this.querySelectorAll('[data-hu-decline]').forEach(button => button.addEventListener('click', () => this.decline(), options));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.decline(); }, options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.decline(); }, options);
      document.addEventListener('click', event => this.checkoutClick(event), { capture: true, signal: this.abortController.signal });
      document.addEventListener('submit', event => this.checkoutSubmit(event), { capture: true, signal: this.abortController.signal });
    }
    disconnectedCallback() {
      this.abortController?.abort();
      document.documentElement.classList.remove('hu-open');
    }
    acknowledged() {
      try { return sessionStorage.getItem('es-hydration-upsell-v2') === 'done'; } catch { return this.done; }
    }
    markDone() {
      this.done = true;
      try { sessionStorage.setItem('es-hydration-upsell-v2', 'done'); } catch {}
    }
    hasOffers() { return this.preview || this.inputs.some(input => input.dataset.ready === 'true'); }
    checkoutClick(event) {
      if (event.defaultPrevented || !event.target.closest) return;
      const target = event.target.closest('#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
      if (!target || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      if (!this.hasOffers() || this.acknowledged()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.beforeCheckout();
    }
    checkoutSubmit(event) {
      if (event.defaultPrevented || !this.hasOffers() || this.acknowledged()) return;
      const submitter = event.submitter;
      if (submitter?.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.beforeCheckout();
    }
    async fetchCart() {
      const response = await fetch(`${this.rootPath}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }
    async beforeCheckout() {
      if (this.pending) return;
      this.pending = true;
      try {
        const cart = await this.fetchCart();
        const hasMainProduct = cart.items.some(item => !this.productIds.has(String(item.product_id)) && item.quantity > 0);
        const hasStick = cart.items.some(item => this.productIds.has(String(item.product_id)));
        if (!hasMainProduct || hasStick) { this.continueCheckout(); return; }
        this.open();
      } catch { this.continueCheckout(); }
      finally { this.pending = false; }
    }
    open() {
      this.returnFocus = document.activeElement;
      this.querySelectorAll('img[data-src]').forEach(img => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('hu-open');
      this.querySelector('h2').focus({ preventScroll: true });
    }
    close() {
      this.dialog.close();
      document.documentElement.classList.remove('hu-open');
      this.returnFocus?.focus?.({ preventScroll: true });
    }
    setStatus(text) {
      const status = this.querySelector('.hu-status');
      status.textContent = text;
      status.hidden = !text;
    }
    setBusy(busy) {
      this.busy = busy;
      this.querySelector('.hu-purchase').disabled = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-hu-decline]').forEach(button => { button.disabled = busy; });
      this.inputs.forEach(input => { input.disabled = busy || (!this.preview && input.dataset.ready !== 'true'); });
    }
    async purchase() {
      if (this.busy) return;
      const selected = this.inputs.filter(input => input.checked && !input.disabled);
      if (!selected.length) { this.setStatus(this.strings.select); return; }
      if (this.preview) { this.setStatus(this.strings.preview); return; }
      this.setBusy(true);
      this.setStatus(this.strings.adding);
      try {
        const cart = await this.fetchCart();
        const existing = new Set(cart.items.map(item => String(item.variant_id)));
        const items = selected.filter(input => !existing.has(input.value)).map(input => ({ id: input.value, quantity: 1 }));
        if (items.length) {
          const response = await fetch(`${this.rootPath}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error('Offer could not be added');
          await response.json();
        }
        this.markDone();
        this.continueCheckout();
      } catch {
        this.setStatus(this.strings.error);
        this.setBusy(false);
      }
    }
    decline() {
      if (this.busy) return;
      if (this.preview) { this.close(); return; }
      this.markDone();
      this.continueCheckout();
    }
    continueCheckout() { window.location.assign(`${this.rootPath}checkout`); }
  }
  customElements.define('hydration-upsell', HydrationUpsell);
})();
